import React from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { handleInvalidMemoSectionRequest } from "utils/sdk";

import { useMemoSignatureRoleList } from "entities/Memo/sdk";
import SectionForm, {
  SectionValuesType,
} from "entities/MemoTemplate/components/SectionForm";
import { EMemoSectionType } from "entities/MemoTemplate/constants";
import {
  IMemoTemplateSection,
  memoSectionUpdateAndAIPromptCreate,
  memoSectionWithSignatureRoleUpdate,
  updateMemoTemplateSection,
  useMemoTemplateDetails,
} from "entities/MemoTemplate/sdk";

const EditSectionForm = ({
  section,
  onCancel,
  onSectionEditted,
  formikRef,
}: {
  section: IMemoTemplateSection;
  onCancel: () => void;
  onSectionEditted: () => void;
  formikRef?: React.Ref<any>;
}) => {
  const params = useParams<{ memoId: string }>();
  const memoId = Number(params.memoId);
  const { mutate: mutateMemo } = useMemoTemplateDetails({
    memo_id: memoId,
  });
  const { mutate: mutateMemoSignatureRoles } = useMemoSignatureRoleList();

  const handleSubmit = (data: SectionValuesType) => {
    if (!_.isNil(data.ai_prompt)) {
      return memoSectionUpdateAndAIPromptCreate({
        memoSectionId: section.memo_section_id,
        data: {
          ai_prompt: data.ai_prompt,
          ..._.pick(data, [
            "title",
            "section_type",
            "break_before",
            "ai_prompt",
          ]),
        },
      })
        .then(() => {
          mutateMemo();
          onSectionEditted();
        })
        .catch(handleInvalidMemoSectionRequest);
    }

    if (
      !_.isNil(data.memo_signature_role) &&
      !_.isNil(data.memo_signature_field_order) &&
      !_.isNil(data.custom_fields)
    ) {
      return memoSectionWithSignatureRoleUpdate({
        memoSectionId: section.memo_section_id,
        data: {
          ..._.pick(data, ["title", "section_type", "break_before"]),
          memo_signature_role: data.memo_signature_role,
          memo_signature_field_order: data.memo_signature_field_order,
          custom_fields: data.custom_fields,
        },
      })
        .then(() => {
          mutateMemo();
          mutateMemoSignatureRoles();
          onSectionEditted();
        })
        .catch(handleInvalidMemoSectionRequest);
    }

    return updateMemoTemplateSection({
      memoSectionId: section.memo_section_id,
      data: {
        ..._.pick(data, [
          "title",
          "section_type",
          "break_before",
          "limit_image_section_height",
          "table_section_compact_view",
          "sources_and_uses_section_display_mode",
          "amenities_section_display_image",
        ]),
        checklist_table: data.checklist_table.map((row, rowIndex) =>
          row.map((value, colIndex) => ({
            value,
            is_editable: data.checklist_table_editable_cells.some(
              (item) => item.row === rowIndex && item.column === colIndex
            ),
          }))
        ),
      },
    }).then(() => {
      mutateMemo();
      onSectionEditted();
    });
  };

  return (
    <SectionForm
      initialValues={{
        ...section,
        due_diligence_section:
          section.due_diligence_section?.due_diligence_section_id,
        due_diligence_package_id:
          section.due_diligence_section?.due_diligence_package_id,
        ai_prompt:
          section.section_type === EMemoSectionType.AI_ANSWER
            ? {
                id: section.ai_prompt?.id,
                title: section.ai_prompt?.title,
                prompt: section.ai_prompt?.prompt,
                source_document_ids: section.ai_prompt?.source_documents.map(
                  (doc) => doc.id
                ),
              }
            : undefined,
        memo_signature_role:
          section.section_type === EMemoSectionType.SIGNATURE
            ? _.get(section, "memo_signature_field.role")
            : undefined,
        memo_signature_field_order:
          section.section_type === EMemoSectionType.SIGNATURE
            ? _.get(section, "memo_signature_field.order")
            : undefined,
        custom_fields:
          section.section_type === EMemoSectionType.SIGNATURE
            ? _.get(section, "memo_signature_field.custom_fields")
            : [],
        checklist_table: section.checklist_table.map((row) =>
          row.map(({ value }) => value)
        ),
        checklist_table_editable_cells: section.checklist_table.flatMap(
          (row, rowIndex) =>
            row
              .map((cell, colIndex) =>
                cell.is_editable
                  ? { row: rowIndex, column: colIndex }
                  : undefined
              )
              .filter((item) => item !== undefined)
        ),
      }}
      isEditForm
      dueDiligenceSection={section.due_diligence_section}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      formikRef={formikRef}
    />
  );
};

export default EditSectionForm;
